import { useLanguageContext } from "../../../context/LanguageContext";
import OverlayLoader from "../../../components/loader/OverlayLoader/OverlayLoader";
import FormContainer from "../../../components/formContainer/FormContainer";
import InputWrapper from "../../../components/input/wrapper/InputWrapper";
import EVAApiHelper from "../../../helpers/EVAApiHelper";
import { StyledInput } from "../../../styles/InputStyles";
import LocationFormSelect from "../../../components/select/custom/location/form/LocationFormSelect";
import MultiSelect from "../../../components/select/main/multi/MultiSelect";
import Select from "../../../components/select/main/Select";
import {
  StyledFlexColumn,
  StyledFlexItem,
  StyledFlexRow,
} from "../../../styles/LayoutStyles";
import LanguageFormSelect from "../../../components/select/custom/language/form/LanguageFormSelect";
import TerminalVisitorTypes from "./visitor_types/TerminalVisitorTypes";
import { useTheme } from "styled-components";

function TerminalPageView({
  terminal,
  terminalData,
  setTerminalData,
  buttons,
  visitorTypes,
  screensavers,
  layouts,
  printers,
  languages,
  onButtonsChange,
  onNameChange,
  onLocationIdChange,
  onLayoutIdChange,
  onPrinterIdChange,
  onLanguageIdChange,
  onScreensaversChange,
  onShowSignInButtonChange,
  onShowSignOutButtonChange,
  onShowQrCodeButtonChange,
  onShowEmployeeRegistrationButtonChange,
  onStatusAfterSignInChange,
  onCancel,
  onSubmit,
  submitError,
}) {
  const theme = useTheme();
  const { translate } = useLanguageContext();

  return (
    <StyledFlexColumn style={{ gap: theme.spacing.large }}>
      <OverlayLoader groups="terminal_page_fetch">
        <FormContainer
          onCancel={onCancel}
          onSubmit={onSubmit}
          submitError={submitError}
          submitLoadingGroup={"terminal_page_save"}
        >
          <StyledFlexColumn>
            <InputWrapper
              label={translate("eva.main.entities.location")}
              error={EVAApiHelper.getResponseError("location_id", submitError)}
              required
            >
              <LocationFormSelect
                value={terminalData.location_id}
                onChange={onLocationIdChange}
                disabled={terminal !== undefined}
                useNullOption={false}
              />
            </InputWrapper>

            <InputWrapper
              label={translate("eva.main.validation.attributes.name")}
              error={EVAApiHelper.getResponseError("name", submitError)}
              required
            >
              <StyledInput
                type="text"
                onChange={onNameChange}
                placeholder={translate("eva.main.validation.attributes.name")}
                value={terminalData.name}
              />
            </InputWrapper>

            <InputWrapper
              label={translate("eva.main.validation.attributes.buttons")}
              error={EVAApiHelper.getResponseErrorArray(
                "buttons",
                submitError,
                (index) =>
                  buttons.find(
                    (button) => terminalData.buttons[index] === button.id,
                  )?.name,
              )}
            >
              <MultiSelect
                options={buttons.map((button) => {
                  return {
                    value: button.id,
                    label: button.name,
                  };
                })}
                value={terminalData.buttons}
                onChange={onButtonsChange}
              />
            </InputWrapper>

            <InputWrapper
              label={translate("eva.main.validation.attributes.screensavers")}
              error={EVAApiHelper.getResponseErrorArray(
                "screensavers",
                submitError,
                (index) =>
                  screensavers.find(
                    (screensaver) =>
                      terminalData.screensavers[index] === screensaver.id,
                  )?.name,
              )}
            >
              <MultiSelect
                options={screensavers.map((screensaver) => {
                  return {
                    value: screensaver.id,
                    label: screensaver.name,
                  };
                })}
                value={terminalData.screensavers}
                onChange={onScreensaversChange}
              />
            </InputWrapper>

            <InputWrapper
              label={translate("eva.main.validation.attributes.layout_id")}
              error={EVAApiHelper.getResponseError("layout_id", submitError)}
            >
              <Select
                options={layouts.map((layout) => ({
                  value: layout.id,
                  label: layout.name,
                }))}
                value={terminalData.layout_id}
                onChange={onLayoutIdChange}
              />
            </InputWrapper>

            <InputWrapper
              label={translate("eva.main.validation.attributes.printer_id")}
              error={EVAApiHelper.getResponseError("printer_id", submitError)}
            >
              <Select
                options={printers.map((printer) => ({
                  value: printer.id,
                  label: printer.name,
                }))}
                nullable
                value={terminalData.printer_id}
                onChange={onPrinterIdChange}
              />
            </InputWrapper>

            <InputWrapper
              label={translate("eva.main.validation.attributes.language_id")}
              error={EVAApiHelper.getResponseError("language_id", submitError)}
            >
              <LanguageFormSelect
                value={terminalData.language_id}
                onChange={onLanguageIdChange}
                nullable={true}
              />
            </InputWrapper>

            <StyledFlexRow>
              <StyledFlexItem>
                <InputWrapper
                  label={translate(
                    "eva.main.validation.attributes.show_sign_in_button",
                  )}
                  error={EVAApiHelper.getResponseError(
                    "show_sign_in_button",
                    submitError,
                  )}
                >
                  <Select
                    options={[
                      { value: true, label: translate("eva.main.general.yes") },
                      { value: false, label: translate("eva.main.general.no") },
                    ]}
                    value={terminalData.show_sign_in_button}
                    onChange={onShowSignInButtonChange}
                  />
                </InputWrapper>
              </StyledFlexItem>

              <StyledFlexItem>
                <InputWrapper
                  label={translate(
                    "eva.main.validation.attributes.show_sign_out_button",
                  )}
                  error={EVAApiHelper.getResponseError(
                    "show_sign_out_button",
                    submitError,
                  )}
                >
                  <Select
                    options={[
                      { value: true, label: translate("eva.main.general.yes") },
                      { value: false, label: translate("eva.main.general.no") },
                    ]}
                    value={terminalData.show_sign_out_button}
                    onChange={onShowSignOutButtonChange}
                  />
                </InputWrapper>
              </StyledFlexItem>

              <StyledFlexItem>
                <InputWrapper
                  label={translate(
                    "eva.main.validation.attributes.show_qr_code_button",
                  )}
                  error={EVAApiHelper.getResponseError(
                    "show_qr_code_button",
                    submitError,
                  )}
                >
                  <Select
                    options={[
                      { value: true, label: translate("eva.main.general.yes") },
                      { value: false, label: translate("eva.main.general.no") },
                    ]}
                    value={terminalData.show_qr_code_button}
                    onChange={onShowQrCodeButtonChange}
                  />
                </InputWrapper>
              </StyledFlexItem>

              <StyledFlexItem>
                <InputWrapper
                  label={translate(
                    "eva.main.validation.attributes.show_employee_registration_button",
                  )}
                  error={EVAApiHelper.getResponseError(
                    "show_employee_registration_button",
                    submitError,
                  )}
                >
                  <Select
                    options={[
                      { value: true, label: translate("eva.main.general.yes") },
                      { value: false, label: translate("eva.main.general.no") },
                    ]}
                    value={terminalData.show_employee_registration_button}
                    onChange={onShowEmployeeRegistrationButtonChange}
                  />
                </InputWrapper>
              </StyledFlexItem>
            </StyledFlexRow>

            <InputWrapper
              label={translate(
                "eva.main.validation.attributes.status_after_sign_in",
              )}
              error={EVAApiHelper.getResponseError(
                "status_after_sign_in",
                submitError,
              )}
            >
              <Select
                options={[
                  {
                    value: "expected",
                    label: translate("eva.main.visitor_statuses.expected"),
                  },
                  {
                    value: "signed_in",
                    label: translate("eva.main.visitor_statuses.signed_in"),
                  },
                ]}
                value={terminalData.status_after_sign_in}
                onChange={onStatusAfterSignInChange}
              />
            </InputWrapper>
          </StyledFlexColumn>
        </FormContainer>
      </OverlayLoader>

      <TerminalVisitorTypes
        terminalData={terminalData}
        setTerminalData={setTerminalData}
        visitorTypes={visitorTypes}
      />
    </StyledFlexColumn>
  );
}

export default TerminalPageView;
